import React, { useState, useRef, useEffect } from "react";
import "./UpdateFieldsFormAICard.scss";
import aiChatlogo from "../../../../../../assets/Images/aiChatlogo.png";
import { orderFieldsNonConfirmedDetailed } from "../../AIChatStaticData/staticFields";
import { ChevronDown } from "../../../../../../assets/Icons/ChevronDown";

export const UpdateFieldsFormAICard = ({
  message,
  onActionClick,
  onFormValuesChange,
}) => {
  const selectedFieldLabels = message.selectedFields || [];

  // Filter the detailed fields based on selected labels
  const fieldsToDisplay = orderFieldsNonConfirmedDetailed.filter((field) =>
    selectedFieldLabels.includes(field.fieldLabel),
  );

  // Group fields by section
  const groupedFields = {
    main: fieldsToDisplay.filter((f) => f.section === "main"),
    date: fieldsToDisplay.filter((f) => f.section === "date"),
    contributor: fieldsToDisplay.filter((f) => f.section === "contributor"),
  };

  // State to hold form values
  const [formValues, setFormValues] = useState(() => {
    const initialValues = {};
    fieldsToDisplay.forEach((field) => {
      initialValues[field.fieldKey] = field.value || "";
    });
    return initialValues;
  });

  // State for custom dropdown
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRefs = useRef({});

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && dropdownRefs.current[openDropdown]) {
        if (!dropdownRefs.current[openDropdown].contains(event.target)) {
          setOpenDropdown(null);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropdown]);

  const now = new Date();
  const day = now.toLocaleDateString(undefined, {
    weekday: "long",
  });
  const time = now.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });

  const handleInputChange = (fieldKey, value) => {
    setFormValues((prev) => {
      const updatedValues = {
        ...prev,
        [fieldKey]: value,
      };

      // Pass updated values to parent if callback exists
      if (onFormValuesChange) {
        onFormValuesChange(updatedValues);
      }

      return updatedValues;
    });
  };

  const handleSubmit = () => {
    if (onActionClick) {
      onActionClick("Submit field updates");
    }
  };

  const renderFieldInput = (field) => {
    if (field.type === "textbox") {
      return (
        <input
          type="text"
          className="field-input"
          value={formValues[field.fieldKey] || ""}
          onChange={(e) => handleInputChange(field.fieldKey, e.target.value)}
          placeholder={`Enter ${field.fieldLabel}`}
        />
      );
    } else if (field.type === "date") {
      return (
        <>
          <input
            type="date"
            className="field-input field-date-input"
            value={formValues[field.fieldKey] || ""}
            onClick={(event) => {
              try {
                event.currentTarget.showPicker?.();
              } catch (error) {}
            }}
            onFocus={(event) => {
              try {
                event.currentTarget.showPicker?.();
              } catch (error) {}
            }}
            onKeyDown={(event) => {
              if (!["Tab", "Shift", "Escape"].includes(event.key)) {
                event.preventDefault();
              }
            }}
            onChange={(e) => handleInputChange(field.fieldKey, e.target.value)}
            inputMode="none"
          />
        </>
      );
    } else if (field.type === "checkbox") {
      return (
        <input
          type="checkbox"
          className="field-checkbox-input"
          checked={formValues[field.fieldKey] || false}
          onChange={(e) => handleInputChange(field.fieldKey, e.target.checked)}
        />
      );
    } else if (field.type === "dropdown") {
      const isOpen = openDropdown === field.fieldKey;
      const selectedValue = formValues[field.fieldKey] || "";
      const displayText = selectedValue || `Select ${field.fieldLabel}`;

      return (
        <div
          className="custom-dropdown-wrapper"
          ref={(el) => (dropdownRefs.current[field.fieldKey] = el)}
        >
          <div
            className="field-input field-dropdown"
            onClick={() => setOpenDropdown(isOpen ? null : field.fieldKey)}
          >
            <span className="dropdown-selected-text" title={displayText}>
              {displayText}
            </span>
            <span className="dropdown-arrow">
              <ChevronDown />
            </span>
          </div>
          {isOpen && (
            <div className="custom-dropdown-menu">
              <div
                className="custom-dropdown-option"
                onClick={() => {
                  handleInputChange(field.fieldKey, "");
                  setOpenDropdown(null);
                }}
              >
                Select {field.fieldLabel}
              </div>
              {field.values?.map((option, index) => (
                <div
                  key={index}
                  className={`custom-dropdown-option ${
                    selectedValue === option ? "selected" : ""
                  }`}
                  onClick={() => {
                    handleInputChange(field.fieldKey, option);
                    setOpenDropdown(null);
                  }}
                  title={option}
                >
                  {option}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className={`order-chat-bubble ${message.role}`}>
      <div className="order-ai-card update-fields-form-card">
        <div className="order-ai-card-header">
          <div className="order-ai-card-title-logo">
            <img
              src={aiChatlogo}
              alt="LOGO"
              aria-hidden="true"
              className="order-ai-logo"
              style={{ width: 32, height: 32 }}
            />
          </div>
          <div className="order-ai-card-title-Avatar">
            <span>{message.header?.title || "Axon AI"}</span>
            <div className="order-ai-card-time">{`${day} ${time}`}</div>
          </div>
        </div>

        <div className="order-ai-card-body">
          <div className="update-fields-form-header">
            <p className="update-fields-form-text">
              Great, Now update the values corresponding to related fields.
            </p>
          </div>

          <div className="update-fields-form-instruction">
            <span className="emoji-icon">👇</span>
            {"  "}
            <p className="update-fields-form-instruction-text">
              Please provide the values.
            </p>
          </div>
          <div className="update-fields-section-line" />

          <div className="update-fields-form-grid">
            {fieldsToDisplay.map((field, idx) => (
              <div className="field-row" key={idx}>
                <label className="field-label">{field.fieldLabel}</label>
                {renderFieldInput(field)}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
